import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ArrowRight,
  Award,
  BookOpen,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Cloud,
  Compass,
  FileText,
  HeadphonesIcon,
  Scale,
  Search,
  Shield,
  UserCog,
} from 'lucide-react';
import { motion } from 'motion/react';

import { DiagramContainer } from '@src/components/DiagramContainer';
import AnswerSection from '@src/components/seo/AnswerSection';
import { Button } from '@src/components/ui/button';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

const BOSSS_BLUE = '#2973a3'; // Updated to match other pages
const BG_COLOR = '#F8FAFC';
const TEXT_COLOR = '#384656';
const DARK_TEXT = '#1C2534'; // For labels

// Helper function for precise line connections in circular layout
const calculateCircularPosition = (
  centerX,
  centerY,
  radius,
  angle,
) => {
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle),
  };
};

// Helper function to calculate line endpoints that touch circle circumference
const calculateLineEndpoints = (
  x1,
  y1,
  r1,
  x2,
  y2,
  r2,
) => {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  return {
    x1: x1 + r1 * Math.cos(angle),
    y1: y1 + r1 * Math.sin(angle),
    x2: x2 - r2 * Math.cos(angle),
    y2: y2 - r2 * Math.sin(angle),
  };
};

// Advisory Services Circular Diagram
function AdvisoryServicesCircularDiagram() {
  const [hoveredService, setHoveredService] = useState(
    null,
  );

  const centerX = 200;
  const centerY = 160; // Moved down to center better in taller viewBox
  const radius = 105; // Optimized for even spacing while keeping circles within bounds
  const centralRadius = 48; // Keep central circle size
  const serviceRadius = 46; // Scaled up by ~12% for better proportion

  const services = [
    {
      label: 'vCISO',
      color: '#0EA5E9',
      icon: UserCog,
      angle: -Math.PI / 2, // Top (12 o'clock) — flagship offering
    },
    {
      label: 'Governance',
      color: '#10B981',
      icon: BookOpen,
      angle: -Math.PI / 2 + Math.PI / 3, // 2 o'clock
    },
    {
      label: 'Risk Assessment',
      color: '#F59E0B',
      icon: Scale,
      angle: -Math.PI / 2 + (2 * Math.PI) / 3, // 4 o'clock
    },
    {
      label: 'Compliance Strategy',
      color: '#EF4444',
      icon: FileText,
      angle: -Math.PI / 2 + Math.PI, // 6 o'clock
    },
    {
      label: 'Identity Roadmap',
      color: '#8B5CF6',
      icon: Compass,
      angle: -Math.PI / 2 + (4 * Math.PI) / 3, // 8 o'clock
    },
    {
      label: 'Cloud Security',
      color: BOSSS_BLUE,
      icon: Cloud,
      angle: -Math.PI / 2 + (5 * Math.PI) / 3, // 10 o'clock
    },
  ];

  const servicePositions = services.map((service) => ({
    ...service,
    ...calculateCircularPosition(
      centerX,
      centerY,
      radius,
      service.angle,
    ),
  }));

  return (
    <DiagramContainer className="w-full max-w-xl mx-auto">
      <svg viewBox="0 0 400 320" className="w-full h-auto">
        {/* Central Hub */}
        <motion.circle
          cx={centerX}
          cy={centerY}
          r={centralRadius}
          fill={BOSSS_BLUE}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            filter: 'drop-shadow(0 6px 20px rgba(41, 115, 163, 0.4))',
          }}
        />

        <motion.text
          x={centerX}
          fill="white"
          fontSize="13"
          y={centerY - 6}
          fontWeight="bold"
          textAnchor="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.9 }}
        >
          BOSSS
        </motion.text>

        <motion.text
          x={centerX}
          fill="white"
          fontSize="11"
          y={centerY + 8}
          fontWeight="bold"
          textAnchor="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.9 }}
        >
          Advisory
        </motion.text>

        {/* Connection lines from center border to service borders */}
        {servicePositions.map((service, i) => {
          const endpoints = calculateLineEndpoints(
            centerX,
            centerY,
            centralRadius,
            service.x,
            service.y,
            serviceRadius,
          );
          return (
            <motion.line
              key={`line-${i}`}
              x1={endpoints.x1}
              y1={endpoints.y1}
              x2={endpoints.x2}
              y2={endpoints.y2}
              strokeWidth="2.5"
              stroke={BOSSS_BLUE}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1, delay: 0.5 + i * 0.1 }}
            />
          );
        })}

        {/* Service Nodes - Even distribution with perfect internal alignment */}
        {servicePositions.map((service, i) => {
          const IconComponent = service.icon;
          return (
            <g key={i}>
              <motion.circle
                fill="white"
                cx={service.x}
                cy={service.y}
                r={serviceRadius}
                strokeWidth="1.5"
                stroke={service.color}
                initial={{ scale: 0 }}
                animate={{
                  scale: hoveredService === service.label ? 1.05 : 1,
                }}
                transition={{
                  duration: 0.5,
                  delay: 0.7 + i * 0.1,
                }}
                style={{
                  filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))',
                  cursor: 'pointer',
                }}
                onMouseLeave={() => setHoveredService(null)}
                onMouseEnter={() => setHoveredService(service.label)}
              />

              {/* Service icon - Centered above label with consistent positioning */}
              <motion.foreignObject
                width="24"
                height="20"
                x={service.x - 12}
                y={service.y - 22}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ pointerEvents: 'none' }}
                transition={{
                  duration: 0.3,
                  delay: 1.0 + i * 0.1,
                }}
              >
                <div className="w-6 h-5 flex items-center justify-center">
                  <IconComponent
                    className="w-5 h-5"
                    style={{
                      color: service.color,
                      strokeWidth: '1.5px',
                    }}
                  />
                </div>
              </motion.foreignObject>

              {/* Service label - Perfectly contained with consistent padding */}
              <motion.foreignObject
                width="76"
                height="32"
                y={service.y + 6}
                x={service.x - 38}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ pointerEvents: 'none' }}
                transition={{
                  duration: 0.3,
                  delay: 1.2 + i * 0.1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    height: '100%',
                    fontSize: '10px',
                    fontWeight: '500',
                    color: DARK_TEXT,
                    lineHeight: '1.2',
                    padding: '3px 4px',
                    overflow: 'hidden',
                  }}
                >
                  {service.label}
                </div>
              </motion.foreignObject>
            </g>
          );
        })}
      </svg>
    </DiagramContainer>
  );
}

function SectionIntro({ title, text }) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-6">
        Advisory Services
      </div>

      <motion.h1
        viewport={{ once: true }}
        style={{ color: DARK_TEXT }}
        transition={{ duration: 0.8 }}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        className="text-4xl lg:text-5xl font-bold mb-4"
      >
        {title}
      </motion.h1>

      <motion.p
        viewport={{ once: true }}
        style={{ color: TEXT_COLOR }}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        className="text-xl max-w-3xl mx-auto"
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        {text}
      </motion.p>
    </div>
  );
}

function ServiceCard({ service }) {
  const ServiceIcon = service.icon;
  return (
    <div
      style={{ backgroundColor: 'white' }}
      className="flex flex-col h-full p-6 rounded-xl border border-gray-200 transition-all duration-200 hover:shadow-xl"
    >
      <div className="flex items-start gap-4 mb-4">
        <div
          style={{ backgroundColor: `${service.color}1a` }}
          className="shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
        >
          <ServiceIcon className="w-6 h-6" style={{ color: service.color }} />
        </div>

        <div className="flex-1">
          <h3 style={{ color: DARK_TEXT }} className="text-xl font-bold">
            {service.title}
          </h3>

          <p style={{ color: TEXT_COLOR }} className="mt-2 text-sm leading-relaxed">
            {service.description}
          </p>
        </div>
      </div>

      <ul className="mt-auto pt-2 grid grid-cols-1 gap-2">
        {service.capabilities.map((capability) => (
          <li key={capability} style={{ color: TEXT_COLOR }} className="flex items-start text-sm">
            <CheckCircle
              style={{ color: service.color }}
              className="w-4 h-4 mr-2 mt-0.5 shrink-0"
            />

            <span>{capability}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Carousel — shows 3 services at a time, auto-advances every 4 seconds, pauses on hover.
// Matches the AgentsCarousel pattern in pages/Platform/AIAgents/AIAgents.jsx.
function ServicesCarousel({ services }) {
  const ITEMS_PER_VIEW = 3;
  const AUTO_SCROLL_INTERVAL = 4000;
  const total = services.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + ITEMS_PER_VIEW) % total);
  }, [total]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - ITEMS_PER_VIEW + total) % total);
  }, [total]);

  const totalPages = Math.ceil(total / ITEMS_PER_VIEW);
  const currentPage = Math.floor(currentIndex / ITEMS_PER_VIEW);

  const goToPage = useCallback((pageIndex) => {
    setCurrentIndex(pageIndex * ITEMS_PER_VIEW);
  }, []);

  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        nextSlide();
      }, AUTO_SCROLL_INTERVAL);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, nextSlide]);

  const visibleServices = [];
  for (let i = 0; i < ITEMS_PER_VIEW; i++) {
    visibleServices.push(services[(currentIndex + i) % total]);
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <button
        style={{ color: DARK_TEXT }}
        aria-label="Previous services"
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white hover:bg-gray-50 border border-gray-200 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-md"
        onClick={prevSlide}
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      <button
        aria-label="Next services"
        style={{ color: DARK_TEXT }}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white hover:bg-gray-50 border border-gray-200 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-md"
        onClick={nextSlide}
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <div className="overflow-hidden mx-8">
        <motion.div
          key={currentIndex}
          exit={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          initial={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch"
        >
          {visibleServices.map((service, index) => (
            <div className="h-full min-h-80" key={`${currentIndex}-${service.title}-${index}`}>
              <ServiceCard service={service} />
            </div>
          ))}
        </motion.div>
      </div>

      <div className="flex justify-center gap-2 mt-8">
        {Array.from({ length: totalPages }).map((_, index) => (
          <button
            key={index}
            aria-label={`Go to page ${index + 1}`}
            style={index === currentPage ? { backgroundColor: BOSSS_BLUE } : undefined}
            className={`h-3 rounded-full transition-all duration-300 ${
              index === currentPage
                ? 'w-8'
                : 'w-3 bg-gray-300 hover:bg-gray-400'
            }`}
            onClick={() => goToPage(index)}
          />
        ))}
      </div>
    </div>
  );
}

const services = [
  {
    icon: UserCog,
    title: 'vCISO',
    color: '#0EA5E9',
    featured: true,
    description:
      'A Virtual CISO (vCISO) gives you executive-level security leadership without the cost or hiring lead-time of a full-time hire. We embed with your leadership team to set strategy, run the security program, own board reporting, '
      + 'and act as the primary point of contact for auditors, customers, and regulators.',
    capabilities: [
      'Security program leadership and roadmap ownership',
      'Board and executive reporting, plus customer security questionnaires',
      'Hiring and management of in-house security staff',
      'Incident response command and breach communications',
      'Auditor and regulator engagement (SOC 2, ISO 27001, PCI, HIPAA)',
    ],
  },
  {
    icon: BookOpen,
    title: 'Governance',
    color: '#10B981',
    description: 'Stand up a security governance program that holds up under audit and scales with the business: charter, policies, standards, control catalog, and the operating cadence that keeps it all current.',
    capabilities: [
      'Policy and standards framework',
      'Control catalog mapped to your obligations',
      'Security committee charter and cadence',
      'Exception and risk-acceptance workflows',
    ],
  },
  {
    icon: Scale,
    title: 'Risk Assessment',
    color: '#F59E0B',
    description: 'Quantify the risks that actually matter. We run risk assessments grounded in your business, prioritize findings by impact and likelihood, and produce a remediation plan your team can execute.',
    capabilities: [
      'Enterprise risk register and heat map',
      'Third-party and vendor risk reviews',
      'Threat modeling for critical applications',
      'Quantified risk reporting for the board',
    ],
  },
  {
    icon: FileText,
    title: 'Compliance Strategy',
    color: '#EF4444',
    description: 'Get and stay compliant without slowing down delivery. We map controls across SOC 2, ISO 27001, PCI DSS, HIPAA, NIST CSF, NIS2, and DORA so a single set of evidence answers many auditors.',
    capabilities: [
      'Multi-framework control mapping',
      'Gap assessment and readiness review',
      'Evidence collection automation',
      'Audit preparation and response',
    ],
  },
  {
    icon: Compass,
    title: 'Identity Roadmap',
    color: '#8B5CF6',
    description: 'Modernize identity and access without breaking what works. We design SSO/MFA rollouts, lifecycle automation, access reviews, and least-privilege models that align with Zero Trust and pass audit scrutiny.',
    capabilities: [
      'IAM/IGA target architecture',
      'Joiner/mover/leaver automation',
      'Access certification campaigns',
      'Privileged access (PAM) and Zero Trust strategy',
    ],
  },
  {
    icon: Cloud,
    title: 'Cloud Security',
    color: BOSSS_BLUE,
    description: 'Secure AWS, Azure, GCP, and Kubernetes environments with a strategy that fits cloud-native realities. We design CSPM, IaC guardrails, secrets management, and workload protection that keep developers fast.',
    capabilities: [
      'CSPM strategy and benchmark selection',
      'IaC guardrails (Terraform, CloudFormation, Kubernetes)',
      'Secrets management and key lifecycle',
      'Cloud incident-response playbooks',
    ],
  },
];

const steps = [
  {
    icon: Calendar,
    title: '1. Strategy & Vision',
    description: 'We partner with your leadership to define a clear cybersecurity vision aligned with your business objectives. This includes a comprehensive assessment of your current security posture.',
  },
  {
    icon: Search,
    title: '2. Risk & Gap Analysis',
    description: 'Our experts perform a deep dive to identify vulnerabilities and compliance gaps in your existing security controls, providing you with a prioritized roadmap for remediation.',
  },
  {
    icon: Compass,
    title: '3. Implementation Guidance',
    description: 'We provide hands-on guidance for implementing recommended solutions, from technology selection to process improvements, ensuring a smooth and effective transition.',
  },
  {
    icon: Award,
    title: '4. Continuous Improvement',
    description: 'Cybersecurity is a journey, not a destination. We provide ongoing advisory support to help you adapt to new threats, regulations, and technological advancements.',
  },
];

export function AdvisoryServicesPage() {
  const onNavigateToTop = useNavigateToTop();
  return (
    <div className="min-h-screen" style={{ backgroundColor: BG_COLOR }} >
      {/* Hero Section */}
      <section className="pt-20 pb-12 lg:pt-32 lg:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Text Content */}
            <motion.div
              className="space-y-8"
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-6">
                  Advisory Services
                </div>

                <h1 style={{ color: DARK_TEXT }} className="text-4xl lg:text-5xl font-bold">
                  Expert Guidance for Your Security Strategy.
                </h1>

                <p style={{ color: TEXT_COLOR }} className="text-xl leading-relaxed">
                  Navigate the complexities of cybersecurity with confidence. Our team of experts provides strategic guidance to build a robust security posture and ensure compliance.
                </p>
              </div>

              <motion.div
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Button
                  style={{ backgroundColor: BOSSS_BLUE, color: 'white' }}
                  className="px-8 py-4 text-lg rounded-lg font-semibold transition-all duration-200 hover:shadow-xl"
                  onClick={() => onNavigateToTop('/demo')}
                >
                  Schedule a Consultation
                </Button>
              </motion.div>
            </motion.div>

            {/* Right Column - Diagram */}
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: 50 }}
            >
              <AdvisoryServicesCircularDiagram />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/services/advisory" />


      {/* Our Advisory Services */}
      <section className="py-12 lg:py-16" style={{ backgroundColor: 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            title="Our Advisory Services"
            text="From executive security leadership to hands-on roadmaps for governance, risk, compliance, identity, and cloud — six advisory engagements that meet you where your program is today."
          />

          {/* Flagship vCISO card */}
          {services
            .filter((s) => s.featured)
            .map((service) => {
              const ServiceIcon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  style={{ backgroundColor: 'white', borderColor: service.color }}
                  className="mt-10 flex flex-col p-6 rounded-xl border-2 transition-all duration-200 hover:shadow-xl"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div
                      style={{ backgroundColor: `${service.color}1a` }}
                      className="shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
                    >
                      <ServiceIcon className="w-6 h-6" style={{ color: service.color }} />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 style={{ color: DARK_TEXT }} className="text-xl font-bold">
                          {service.title}
                        </h3>

                        <span
                          className="text-xs font-semibold px-2 py-1 rounded-full"
                          style={{
                            backgroundColor: `${service.color}1a`,
                            color: service.color,
                          }}
                        >
                          Flagship Engagement
                        </span>
                      </div>

                      <p style={{ color: TEXT_COLOR }} className="mt-2 text-sm leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </div>

                  <ul className="mt-2 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {service.capabilities.map((capability) => (
                      <li key={capability} style={{ color: TEXT_COLOR }} className="flex items-start text-sm">
                        <CheckCircle
                          style={{ color: service.color }}
                          className="w-4 h-4 mr-2 mt-0.5 shrink-0"
                        />

                        <span>{capability}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}

          {/* Other services in a carousel */}
          <div className="mt-8">
            <ServicesCarousel services={services.filter((s) => !s.featured)} />
          </div>
        </div>
      </section>

      {/* Why Advisory Section */}
      <section className="py-12 lg:py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            title="Why Choose Our Advisory Services?"
            text="Our strategic approach helps you proactively manage risk, accelerate compliance, and optimize your security investments."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-col items-center text-center p-6"
            >
              <Clock className="w-12 h-12 mb-4" style={{ color: BOSSS_BLUE }} />
              <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">Reduce Time-to-Value</h3>

              <p className="text-sm" style={{ color: TEXT_COLOR }}>
                Accelerate your security initiatives with expert guidance.
              </p>
            </motion.div>

            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col items-center text-center p-6"
            >
              <Shield className="w-12 h-12 mb-4" style={{ color: BOSSS_BLUE }} />
              <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">Enhance Your Posture</h3>

              <p className="text-sm" style={{ color: TEXT_COLOR }}>
                Proactively identify and address risks before they become a problem.
              </p>
            </motion.div>

            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col items-center text-center p-6"
            >
              <HeadphonesIcon className="w-12 h-12 mb-4" style={{ color: BOSSS_BLUE }} />
              <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">Get Expert Support</h3>

              <p className="text-sm" style={{ color: TEXT_COLOR }}>
                Access a team of seasoned professionals with deep industry knowledge.
              </p>
            </motion.div>

            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col items-center text-center p-6"
            >
              <Award className="w-12 h-12 mb-4" style={{ color: BOSSS_BLUE }} />
              <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">Achieve & Maintain Compliance</h3>

              <p className="text-sm" style={{ color: TEXT_COLOR }}>
                Stay ahead of changing regulations with a future-proof strategy.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            title="Our Four-Step Advisory Process"
            text="We follow a proven methodology to ensure our guidance is actionable, strategic, and perfectly aligned with your business needs."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                style={{ backgroundColor: 'white' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex flex-col items-center text-center p-6 rounded-xl border border-gray-200"
              >
                <div
                  style={{ backgroundColor: `${BOSSS_BLUE}1a` }} // 1a = ~10% opacity in hex
                  className="relative w-16 h-16 rounded-full flex items-center justify-center mb-4"
                >
                  <div
                    className="absolute w-12 h-12 rounded-full"
                    style={{ backgroundColor: `${BOSSS_BLUE}0d` }} // 0d = ~5% opacity
                  />

                  <div
                    className="absolute w-8 h-8 rounded-full"
                    style={{ backgroundColor: `${BOSSS_BLUE}1a` }}
                  />

                  <div
                    style={{ backgroundColor: `${BOSSS_BLUE}33` }} // 33 = ~20% opacity
                    className="relative z-10 p-2 rounded-full flex items-center justify-center"
                  >
                    <step.icon className="w-6 h-6" style={{ color: BOSSS_BLUE }} />
                  </div>
                </div>

                <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">{step.title}</h3>
                <p className="text-sm" style={{ color: TEXT_COLOR }}>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section
        className="py-16 lg:py-24"
        style={{
          background: `linear-gradient(to bottom, transparent, ${BOSSS_BLUE}10)`,
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2
              style={{ color: TEXT_COLOR }}
              className="text-3xl lg:text-4xl font-bold mb-6"
            >
              Ready to Strengthen Your Security Strategy?
            </h2>

            <p
              className="text-xl mb-8 max-w-2xl mx-auto"
              style={{ color: TEXT_COLOR, opacity: 0.8 }}
            >
              Let our cybersecurity experts help you build a comprehensive strategy that addresses your unique challenges and regulatory requirements. Contact us today to discuss your advisory needs.
            </p>

            <motion.div
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.05 }}
            >
              <Button
                style={{ backgroundColor: BOSSS_BLUE }}
                className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                onClick={() => onNavigateToTop('/contact')}
              >
                Start Your Strategic Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default AdvisoryServicesPage;
